import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, TrendingUp, Layers } from "lucide-react";
import { api } from "@/lib/api";
import eventStream from "@/lib/eventStream";

/**
 * Network-wide AI-spend widget on the operator dashboard.
 *
 * Live via SSE topic `network_costs` (30s cadence). Polls
 * `/api/admin/network-costs` once on mount + as the SSE fallback.
 */
export default function NetworkCostsWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/network-costs").then(r => setData(r.data)).catch(() => {});
    const unsub = eventStream.subscribe(
      "network_costs",
      (snap) => setData((prev) => ({ ...(prev || {}), ...snap, by_site: snap.top_3 || prev?.by_site })),
      async () => {
        const r = await api.get("/admin/network-costs");
        return r.data;
      },
    );
    return unsub;
  }, []);

  const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  const t = data?.totals;

  return (
    <div data-testid="network-costs-widget" className="border border-zinc-800 bg-zinc-900/40 rounded-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div>
          <div className="label-tiny">Network AI Spend</div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">All sites · all time</div>
        </div>
        <Link
          data-testid="network-costs-view-sites-link"
          to="/admin/sites"
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 font-mono"
        >
          View sites →
        </Link>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-4 gap-px bg-zinc-800/50 border-b border-zinc-800">
        <Cell tid="net-cost-lifetime" label="Lifetime" value={usd(t?.lifetime_usd)} icon={Layers} accent="#fafafa" />
        <Cell tid="net-cost-month"    label="This month" value={usd(t?.this_month_usd)} icon={TrendingUp} accent="#f59e0b" />
        <Cell tid="net-cost-30d"      label="Last 30d" value={usd(t?.last_30d_usd)} accent="#10b981" />
        <Cell tid="net-cost-today"    label="Today" value={usd(t?.today_usd)} icon={DollarSign} />
      </div>

      {/* Top spenders */}
      <div className="px-5 py-3 border-b border-zinc-800 flex items-baseline justify-between">
        <div className="label-tiny">Top spenders</div>
        {t && (
          <div className="text-[11px] text-zinc-500 font-mono">
            {t.articles_total?.toLocaleString()} articles · avg {usd(t.avg_cost_per_article_usd)}
          </div>
        )}
      </div>
      <div className="divide-y divide-zinc-800">
        {(data?.by_site || []).slice(0, 5).map((s) => (
          <Link
            key={s.site_id}
            data-testid={`network-costs-row-${s.slug || s.site_id}`}
            to={`/admin/sites/${s.site_id}`}
            className="flex items-center justify-between px-5 py-2.5 hover:bg-zinc-900/60 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-100 truncate">{s.name || s.slug}</div>
              {s.domain && (
                <div className="text-[11px] mono text-zinc-500 truncate">{s.domain}</div>
              )}
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <div className="text-[11px] text-zinc-500 font-mono tabular-nums">
                {s.articles.toLocaleString()} articles
              </div>
              <div className="font-mono text-zinc-100 tabular-nums w-20 text-right">
                {usd(s.cost_usd)}
              </div>
            </div>
          </Link>
        ))}
        {data && (data.by_site || []).length === 0 && (
          <div className="px-5 py-6 text-sm text-zinc-500 font-mono">No AI spend yet.</div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, icon: Icon, accent, tid }) {
  return (
    <div data-testid={tid} className="px-4 py-3 bg-zinc-900/40">
      <div className="label-tiny flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={1.75} style={{ color: accent || "#71717a" }} />}
        {label}
      </div>
      <div className="font-display text-xl font-black mt-1 tabular-nums" style={{ color: accent || "#fafafa" }}>
        {value}
      </div>
    </div>
  );
}
